/**
 * RemoteCommandsProvider - wraps simple RemoteCommands into WarpGrepProvider
 * 
 * Handles parsing of raw stdout from grep/read/listDir commands.
 * Users just return stdout, SDK handles all format conversion.
 */

import type { WarpGrepProvider, GrepResult, ReadResult, GlobResult, ListDirectoryEntry } from './types.js';
import type { RemoteCommands } from '../types.js';
import { AGENT_CONFIG } from '../agent/config.js';

/**
 * Directories/files to always skip (exact name match)
 */
const SKIP_NAMES = new Set([
  // Version control
  '.git', '.svn', '.hg', '.bzr',
  // Dependencies
  'node_modules', 'bower_components', '.pnpm', '.yarn',
  'vendor', 'Pods', '.bundle',
  // Python
  '__pycache__', '.pytest_cache', '.mypy_cache', '.ruff_cache',
  '.venv', 'venv', '.tox', '.nox', '.eggs',
  // Build outputs
  'dist', 'build', 'out', 'output', 'target', '_build',
  '.next', '.nuxt', '.output', '.vercel', '.netlify',
  // Cache
  '.cache', '.parcel-cache', '.turbo', '.nx', '.gradle',
  // IDE
  '.idea', '.vscode', '.vs',
  // Coverage
  'coverage', '.coverage', 'htmlcov', '.nyc_output',
  // Temp
  'tmp', 'temp', '.tmp', '.temp',
  // Lock files
  'package-lock.json', 'yarn.lock', 'pnpm-lock.yaml', 'bun.lockb',
  'Cargo.lock', 'Gemfile.lock', 'poetry.lock',
]);

/**
 * File extensions to skip
 */
const SKIP_EXTENSIONS = new Set([
  '.min.js', '.min.css', '.bundle.js',
  '.wasm', '.so', '.dll', '.pyc',
  '.map', '.js.map',
]);

/**
 * Check if a filename should be skipped
 */
function shouldSkip(name: string): boolean {
  // Skip exact name matches
  if (SKIP_NAMES.has(name)) return true;
  
  // Skip hidden files/directories (start with .)
  if (name.startsWith('.')) return true;
  
  // Skip files with certain extensions
  for (const ext of SKIP_EXTENSIONS) {
    if (name.endsWith(ext)) return true;
  }
  
  return false;
}

/**
 * Wraps simple RemoteCommands functions into a full WarpGrepProvider.
 * 
 * This allows users to provide three simple functions that return raw stdout,
 * and the SDK handles all parsing internally.
 * 
 * @example
 * ```typescript
 * const provider = new RemoteCommandsProvider('/home/repo', {
 *   grep: async (pattern, path) => {
 *     const r = await sandbox.run(`rg '${pattern}' '${path}'`);
 *     return r.stdout;
 *   },
 *   read: async (path, start, end) => {
 *     const r = await sandbox.run(`sed -n '${start},${end}p' '${path}'`);
 *     return r.stdout;
 *   },
 *   listDir: async (path, maxDepth) => {
 *     const r = await sandbox.run(`find '${path}' -maxdepth ${maxDepth}`);
 *     return r.stdout;
 *   },
 * });
 * ```
 */
export class RemoteCommandsProvider implements WarpGrepProvider {
  constructor(
    private readonly repoRoot: string,
    private readonly commands: RemoteCommands
  ) {}

  /**
   * Run grep command and parse ripgrep output
   */
  async grep(params: { pattern: string; path: string; glob?: string }): Promise<GrepResult> {
    try {
      const stdout = await this.commands.grep(params.pattern, params.path, params.glob);
      
      // Parse ripgrep output: each line is "path:line:content" or "path-line-content" for context
      const lines = (stdout || '')
        .trim()
        .split(/\r?\n/)
        .filter((l) => l.length > 0);
      
      // Truncate if too many lines (return partial results like training harness)
      if (lines.length > AGENT_CONFIG.MAX_OUTPUT_LINES) {
        const truncated = lines.slice(0, AGENT_CONFIG.MAX_OUTPUT_LINES);
        truncated.push(`... (output truncated at ${AGENT_CONFIG.MAX_OUTPUT_LINES} of ${lines.length} lines)`);
        return { lines: truncated };
      }
      
      return { lines };
    } catch (error) {
      return {
        lines: [],
        error: `[GREP ERROR] ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  /**
   * Read file and add line numbers
   */
  async read(params: { path: string; start?: number; end?: number }): Promise<ReadResult> {
    const rawStart = params.start;
    const rawEnd = params.end;
    
    const startValid = rawStart === undefined || (Number.isFinite(rawStart) && rawStart > 0);
    const endValid = rawEnd === undefined || (Number.isFinite(rawEnd) && rawEnd > 0);
    const rangeValid = startValid && endValid && 
      (rawStart === undefined || rawEnd === undefined || rawStart <= rawEnd);
    
    const start = rangeValid && rawStart !== undefined ? rawStart : 1;
    const end = rangeValid && rawEnd !== undefined ? rawEnd : 1_000_000;
    
    try {
      const stdout = await this.commands.read(params.path, start, end);
      
      // Split content into lines and add line numbers
      const contentLines = (stdout || '').split('\n');
      
      // Remove trailing empty line if present (common with sed output)
      if (contentLines.length > 0 && contentLines[contentLines.length - 1] === '') {
        contentLines.pop();
      }
      
      // Format as "lineNumber|content"
      const lines = contentLines.map((content, idx) => `${start + idx}|${content}`);
      
      // Check if output is too large
      if (lines.length > AGENT_CONFIG.MAX_READ_LINES) {
        const truncated = lines.slice(0, AGENT_CONFIG.MAX_READ_LINES);
        truncated.push(`... (output truncated at ${AGENT_CONFIG.MAX_READ_LINES} of ${lines.length} lines)`);
        return { lines: truncated };
      }
      
      return { lines };
    } catch (error) {
      return {
        lines: [],
        error: `[READ ERROR] ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  /**
   * List directory and parse find output
   */
  async listDirectory(params: { 
    path: string; 
    pattern?: string | null; 
    maxResults?: number; 
    maxDepth?: number;
  }): Promise<ListDirectoryEntry[]> {
    const maxDepth = params.maxDepth ?? AGENT_CONFIG.MAX_LIST_DEPTH;
    const maxResults = params.maxResults ?? AGENT_CONFIG.MAX_OUTPUT_LINES;
    
    try {
      const stdout = await this.commands.listDir(params.path, maxDepth);
      
      // Parse find output: one path per line
      const paths = (stdout || '')
        .trim()
        .split(/\r?\n/)
        .filter((p) => p.length > 0);
      
      const regex = params.pattern ? new RegExp(params.pattern) : null;
      const entries: ListDirectoryEntry[] = [];
      
      for (const fullPath of paths) {
        // Skip the root path itself
        if (fullPath === params.path || fullPath === this.repoRoot) continue;
        
        const name = fullPath.split('/').pop() || '';
        
        // Simple name-based filtering - skip junk dirs/files
        if (shouldSkip(name)) continue;
        
        // Apply pattern filter if provided
        if (regex && !regex.test(name)) continue;
        
        // Determine relative path
        let relativePath = fullPath;
        if (fullPath.startsWith(this.repoRoot)) {
          relativePath = fullPath.slice(this.repoRoot.length).replace(/^\//, '');
        }
        
        // Calculate depth based on path separators
        const depth = relativePath.split('/').filter(Boolean).length - 1;
        
        // Determine if it's a directory (ends with / or infer from path structure)
        // Note: Most sandbox find commands don't indicate type, so we guess based on extension
        const hasExtension = name.includes('.') && !name.startsWith('.');
        const type: 'file' | 'dir' = hasExtension ? 'file' : 'dir';
        
        entries.push({
          name,
          path: relativePath,
          type,
          depth: Math.max(0, depth),
        });
        
        if (entries.length >= maxResults) break;
      }
      
      return entries;
    } catch (error) {
      // Return empty array on error (consistent with LocalRipgrepProvider)
      return [];
    }
  }

  /**
   * Glob search - finds files matching a pattern.
   * Falls back to a grep --files approach via the listDir command.
   */
  async glob(params: { pattern: string; path?: string }): Promise<GlobResult> {
    const searchPath = params.path || this.repoRoot;
    try {
      // Use find with -name to simulate glob
      const stdout = await this.commands.listDir(searchPath, 10);
      const allPaths = (stdout || '')
        .trim()
        .split(/\r?\n/)
        .filter((p) => p.length > 0);

      // Simple glob matching: convert glob pattern to regex
      const globToRegex = (glob: string) => {
        const escaped = glob
          .replace(/[.+^${}()|[\]\\]/g, '\\$&')
          .replace(/\*/g, '.*')
          .replace(/\?/g, '.');
        return new RegExp(escaped);
      };
      const regex = globToRegex(params.pattern);

      const matched = allPaths
        .filter(p => {
          const name = p.split('/').pop() || '';
          return regex.test(name) && !shouldSkip(name);
        });

      const totalFound = matched.length;
      return { files: matched.slice(0, 100), searchDir: searchPath, totalFound };
    } catch (error) {
      return {
        files: [],
        searchDir: searchPath,
        totalFound: 0,
        error: `[GLOB ERROR] ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }
}
